import { Fragment, ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { InflationTooltipContent } from '../projectionTable/cells';
import { currency } from '../projectionTable/formatters';
import { milestoneBadgeClass } from '../projectionTable/milestoneColors';

// Sticky left cell: policy year, calendar year, client age, plus a "Care
// begins" badge on the final row (the policy year LTC month one lands in).
export function AccumulationWhenCell({ row }: { row: AccumulationYear }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-semibold leading-snug text-gray-900">
        Year {row.policyYear}
      </span>
      <span className="text-[10px] text-gray-500">
        {row.calendarYear}
        {row.clientAge != null && ` · age ${row.clientAge}`}
      </span>
      {row.isCareStartYear && (
        <span
          className={`w-fit whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${milestoneBadgeClass('ltc-begins')}`}
        >
          Care begins
        </span>
      )}
    </div>
  );
}

// A growing-balance cell: the value this year, a year-over-year increase
// subtext, and a hover explaining the growth. `tooltip` is the derivation
// block (single-rate inflation for pool-based products via
// `buildInflationTooltip`, or a strategy-specific block for annuities).
export function GrowthValueCell({
  value,
  prevValue,
  tooltip,
}: {
  value: number;
  prevValue: number | null;
  tooltip?: ReactNode;
}) {
  const delta = prevValue != null ? value - prevValue : 0;
  const body = (
    <div className="leading-tight">
      <div className="font-medium tabular-nums text-gray-900">{currency(value)}</div>
      {delta > 0.5 && (
        <div className="text-[10px] tabular-nums text-emerald-600">+{currency(delta)}</div>
      )}
    </div>
  );
  if (!tooltip) return body;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">{body}</div>
      </TooltipTrigger>
      {tooltip}
    </Tooltip>
  );
}

// Closed-form inflation derivation for a balance that grows on a single
// per-year rate (pool-based products). The exponent matches the engine's
// `compoundFactor`: increments = policyYear − 1, capped at `capYears`.
export function buildInflationTooltip({
  startingLabel,
  startingValue,
  rate,
  capYears,
  isSimple,
  policyYear,
  result,
  startYear,
}: {
  startingLabel: string;
  startingValue: number;
  rate: number;
  capYears: number;
  isSimple: boolean;
  policyYear: number;
  result: number;
  startYear: number;
}): ReactNode {
  const cap = capYears > 0 ? capYears : null;
  const rawIncrements = Math.max(Math.floor(policyYear) - 1, 0);
  const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
  const capped = cap !== null && Math.floor(policyYear) > cap;
  return (
    <InflationTooltipContent
      header={`Policy year ${Math.floor(policyYear)} · started ${startYear}`}
      startingLabel={startingLabel}
      startingValue={startingValue}
      rate={rate}
      years={increments}
      result={result}
      isSimple={isSimple}
      note={capped ? `Inflation capped at year ${cap}` : undefined}
    />
  );
}

// Simple at-issue → this-year block for balances whose growth is NOT a single
// closed-form rate (annuity contract/account values credited on guaranteed or
// indexed schedules). States the start and current values and the labelled
// driver without asserting a (1+r)^n formula that wouldn't reconcile.
export function ScheduleGrowthTooltip({
  startingLabel,
  startingValue,
  result,
  driverLabel,
  rows,
}: {
  startingLabel: string;
  startingValue: number;
  result: number;
  driverLabel: string;
  rows?: Array<[string, number]>;
}): ReactNode {
  return (
    <TooltipContent className="text-xs">
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">{startingLabel}</span>
        <span className="font-semibold tabular-nums">{currency(startingValue)}</span>
        <span className="text-gray-300">This policy year</span>
        <span className="font-semibold tabular-nums">{currency(result)}</span>
        <span className="text-gray-300">Growth</span>
        <span className="font-semibold">{driverLabel}</span>
        {rows?.map(([label, value]) => (
          <Fragment key={label}>
            <span className="text-gray-300">{label}</span>
            <span className="font-semibold tabular-nums">{currency(value)}</span>
          </Fragment>
        ))}
      </div>
    </TooltipContent>
  );
}
